import React, { useState, useContext, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { render } from 'storyblok-rich-text-react-renderer';
import { Card, Badge } from 'react-bootstrap';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import Button from 'react-bootstrap/Button';
import PostModal from '../BlogPostModal/PostModal';
import Utils from '../../services/utils';
import i18next from '../../translations/i18n';
import AppTypeContext from '../../contexts/AppTypeContext';
import UnderlyingCard from '../BlogPostModal/UnderlyingCard';
import API from '../../services/API';
import { setSliderHeight } from '../../redux/slices';
import AuthorImage from '../AuthorImage';
import ImageModalPopup from '../ImageModalPopup';
import './index.scss';

const ShareIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    data-prefix="fas"
    data-icon="copy"
    className="svg-inline--fa fa-copy"
    role="img"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 448 512"
  >
    <path
      fill="currentColor"
      d="M208 0L332.1 0c12.7 0 24.9 5.1 33.9 14.1l67.9 67.9c9 9 14.1 21.2 14.1 33.9L448 336c0 26.5-21.5 48-48 48l-192 0c-26.5 0-48-21.5-48-48l0-288c0-26.5 21.5-48 48-48zM48 128l80 0 0 64-64 0 0 256 192 0 0-32 64 0 0 48c0 26.5-21.5 48-48 48L48 512c-26.5 0-48-21.5-48-48L0 176c0-26.5 21.5-48 48-48z"
    />
  </svg>
);

const PostBlok = ({ post, isSmallDevice, handleShare }) => {
  const textRef = useRef(null);
  const cardRef = useRef(null);
  const dispatch = useDispatch();

  const { data: assetClassesList } = useSelector(
    (state) => ({
      data: state.app.data,
    }),
    shallowEqual,
  );

  const {
    postCreatedDate,
    postTitle,
    postDescriptionText,
    underlyings = [],
    externalFileLinks = [],
    assetClasses = [],
    author,
    chartImage = {},
    _uid,
    source = '',
  } = post;

  const filename = chartImage.filename || '';
  const imageUrl = filename.replace('//a.storyblok.com', '//a2.storyblok.com');
  const [showAppPostModal, setAppPostModal] = useState(false);
  const appName = useContext(AppTypeContext);
  const maxHeight = '130px';

  const formattedName = useMemo(() => {
    if (author) {
      const fileName = author.split('/').pop().split('.')[0];
      return fileName
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    return 'WisdomTree';
  }, [author]);

  const filteredUnderlyings = useMemo(() => {
    const flattenUnderlyingList = assetClassesList.reduce((arr, elem) => {
      elem.groups.forEach((group) => arr.push(...group.underlyingList));
      return arr;
    }, []);
    return flattenUnderlyingList.filter((item) =>
      underlyings.includes(item.ticker),
    );
  }, [assetClassesList, underlyings]);

  useEffect(() => {
    const updateHeight = async () => {
      if (cardRef.current) {
        const height = cardRef.current.scrollHeight;
        await dispatch(setSliderHeight(height * 1.5));
      }
    };

    const resizeObserver = new ResizeObserver(updateHeight);
    if (cardRef.current) resizeObserver.observe(cardRef.current);
    updateHeight();

    return () => resizeObserver.disconnect();
  }, [dispatch]);

  const getTextURL = (fileUrl) => {
    try {
      return new URL(fileUrl).href;
    } catch {
      const { origin } = window.location;
      const { isoCode = 'en-GB' } = window.ttCentre;
      return `${origin}/${isoCode.toLowerCase()}/${fileUrl}`;
    }
  };

  return (
    <>
      <Card className="border-0 shadow h-100 m-3" ref={cardRef}>
        <Card.Body className="post-body">
          <Card.Title as="h3" className="card-post-title">
            {postTitle}{' '}
          </Card.Title>
          <div className="d-flex justify-content-between">
            <div className="d-flex align-items-center py-2">
              {author && <AuthorImage authorURL={author} alt={formattedName} />}
              {!author && <div className="author-image not-available" />}
              <div>
                <p className="text-teal fw-bold mb-0">
                  by {formattedName || 'WisdomTree'}
                </p>
                <p className="text-muted mb-0">
                  {' '}
                  {postCreatedDate ? Utils.formatDate(postCreatedDate) : ''}
                </p>
              </div>
            </div>
            <div className="d-flex flex-column text-center text-source">
              <Button
                onClick={() => handleShare(_uid)}
                className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 share-icon copy mb-2"
                variant="outline-secondary"
                size="sm"
                aria-label={i18next.t('share')}
              >
                <ShareIcon />
              </Button>
              {i18next.t('Share')}
            </div>
          </div>

          <Card.Text as="div" className="block-with-text pt-1 m-0">
            {isSmallDevice && (
              <div
                ref={textRef}
                className={`text-truncate-container ${'expanded'}`}
                style={{ maxHeight, transition: 'max-height 0.3s ease' }}
              >
                {render(postDescriptionText)}
              </div>
            )}
            {!isSmallDevice && (
              <>
                {render(postDescriptionText)}
                {filename.includes('https') && (
                  <section className="text-center">
                    <ImageModalPopup imageUrl={imageUrl} />
                  </section>
                )}
                {source && (
                  <div className="row mt-3">
                    <div className="col">
                      <Card.Text className="text-source">
                        {i18next.t('Source')} : {source}
                      </Card.Text>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card.Text>

          {isSmallDevice &&
            textRef.current &&
            (textRef.current.scrollHeight > 125 || imageUrl) && (
              <Card.Link
                className="link-teal fw-bold py-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  API.sendToGA(
                    postTitle
                      ? `${appName}-read-${postTitle}`
                      : `${appName}-read-no-title`,
                  );
                  if (isSmallDevice) {
                    setAppPostModal(true);
                  }
                }}
                name={postTitle}
                title={`${i18next.t('clickToView')} ${postTitle}`}
              >
                {i18next.t('viewMore')}
              </Card.Link>
            )}

          <div className="desktop d-none d-md-block">
            <div className="row mt-3">
              <div className="col-12 col-md-6">
                {externalFileLinks.length > 0 &&
                  externalFileLinks.map((pdf) => (
                    <div
                      key={pdf._uid}
                      className="align-middle download-container"
                    >
                      <Card.Link
                        className={`mt-auto text-blue text-decoration-none pdf-link-ttcentre post-file-link align-middle ${
                          pdf.pdfFileUrl.url.indexOf('.pdf') !== -1
                            ? 'pdf-link-ttc'
                            : 'post-file-link'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          API.sendToGA(
                            getTextURL(pdf.pdfFileUrl.url),
                            'pdf downloads',
                          );
                          window.open(getTextURL(pdf.pdfFileUrl.url), '_blank');
                        }}
                        target="_blank"
                      >
                        {`${pdf.fileName}.${
                          // eslint-disable-next-line no-useless-escape
                          pdf.pdfFileUrl.url
                            .split('.')
                            .pop()
                            // eslint-disable-next-line no-useless-escape
                            .split(/\#|\?/)[1] || ''
                        }`}
                      </Card.Link>
                    </div>
                  ))}
              </div>
              <div className="col-12 col-md-6 d-flex gap-2 align-items-center flex-wrap p-0 justify-content-end tags">
                {assetClasses.map((aClass) => (
                  <Badge
                    bg="text-bg-light"
                    text="dark"
                    className="d-inline border p-2 px-3 fw-normal gap-4 tag"
                    key={aClass}
                  >
                    {aClass}
                  </Badge>
                ))}
              </div>
            </div>
            {!isSmallDevice && (
              <div className="mt-4 d-flex gap-4 flex-wrap">
                {filteredUnderlyings.map((underlying) => (
                  <UnderlyingCard
                    underlying={underlying}
                    key={_uid + underlying.name}
                    setOpenModal={() => true}
                    postTitle={postTitle}
                    postId={_uid}
                  />
                ))}
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
      {showAppPostModal && (
        <PostModal
          onHide={() => setAppPostModal(false)}
          post={post}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            setAppPostModal(false);
          }}
        />
      )}
    </>
  );
};

PostBlok.propTypes = {
  post: PropTypes.shape({
    postCreatedDate: PropTypes.string.isRequired,
    postTitle: PropTypes.string.isRequired,
    postDescriptionText: PropTypes.shape({}).isRequired,
    chartImage: PropTypes.shape({
      filename: PropTypes.string.isRequired,
    }),
    underlyings: PropTypes.arrayOf(PropTypes.string).isRequired,
    externalFileLinks: PropTypes.arrayOf(PropTypes.shape([]).isRequired),
    author: PropTypes.string.isRequired,
    assetClasses: PropTypes.arrayOf(PropTypes.shape([]).isRequired),
    _uid: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired,
  }).isRequired,
  isSmallDevice: PropTypes.bool.isRequired,
  handleShare: PropTypes.func.isRequired,
};

export default PostBlok;
